import React, { useMemo, useEffect } from 'react';
import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';
import { FormContainer, FormRow } from '../../../components/form';
import { ButtonAdd } from '../../../components/Buttons';
import { LineDivider } from '../../../components/LineDivider';
import { TranslationTable } from './TranslationTable';
import type { LanguageTranslationsResponse, LanguageTranslationsEditRequest } from '../../../../api';
import './../Translations.css';

export interface LanguageTranslationsFormProps {
    translations: LanguageTranslationsResponse | null;
    onSubmit: (data: LanguageTranslationsEditRequest) => Promise<void> | void;
    processing: boolean;
}

export function LanguageTranslationsForm({
    translations,
    onSubmit,
    processing
}: LanguageTranslationsFormProps) {

    const defaultValues = useMemo<LanguageTranslationsResponse>(() => ({
        vote_results: translations?.vote_results ?? [],
        document_types: translations?.document_types ?? [],
        vote_options: translations?.vote_options ?? [],
        attendances: translations?.attendances ?? [],
        body_types: translations?.body_types ?? [],
        municipality_types: translations?.municipality_types ?? []
    }), [translations]);

    const { control, handleSubmit, reset } = useForm<LanguageTranslationsResponse>({
        defaultValues,
        disabled: processing
    });

    useEffect(() => {
        if (translations) {
            reset(defaultValues);
        }
    }, [translations, reset, defaultValues]);

    const { fields: voteResultFields } = useFieldArray({ control, name: 'vote_results' });
    const { fields: docTypeFields } = useFieldArray({ control, name: 'document_types' });
    const { fields: voteOptionFields } = useFieldArray({ control, name: 'vote_options' });
    const { fields: attendanceFields } = useFieldArray({ control, name: 'attendances' });
    const { fields: bodyTypeFields } = useFieldArray({ control, name: 'body_types' });
    const { fields: municipalityTypeFields } = useFieldArray({ control, name: 'municipality_types' });

    const handleFormSubmit: SubmitHandler<LanguageTranslationsResponse> = (data) => {
        const payload: LanguageTranslationsEditRequest = {
            vote_results: data.vote_results.map(({ option_id, vote_result_text }) => ({
                option_id,
                vote_result_text
            })),
            document_types: data.document_types.map(({ option_id, document_type_text }) => ({
                option_id,
                document_type_text
            })),
            vote_options: data.vote_options.map(({ option_id, male_text, female_text, universal_text }) => ({
                option_id,
                male_text: male_text ?? '',
                female_text,
                universal_text
            })),
            attendances: data.attendances.map(({ option_id, attendance_text }) => ({
                option_id,
                attendance_text
            })),
            body_types: data.body_types.map(({ option_id, typ_organu_text, typ_organu_text_genitiv }) => ({
                option_id,
                typ_organu_text,
                typ_organu_text_genitiv
            })),
            municipality_types: data.municipality_types.map(({ option_id, typ_municipality_text, typ_municipality_text_genitiv }) => ({
                option_id,
                typ_municipality_text,
                typ_municipality_text_genitiv
            }))
        };

        onSubmit(payload);
    };
    
    return (
        <form onSubmit={handleSubmit(handleFormSubmit)}>
            <FormContainer>
                <div className='translation-content'>
                    <div className='translation-grid'>
                        <div className='translation-section'>
                            <h3 className='translation-section__title'>Výsledky hlasování</h3>
                            <TranslationTable
                                fields={voteResultFields}
                                control={control}
                                arrayName="vote_results"
                                identityColumns={[
                                    { field: 'option_id', header: 'ID' },
                                    { field: 'code', header: 'Kód' }
                                ]}
                                translationColumns={[{ field: 'vote_result_text', header: 'Text překladu', required: true }]}
                                variant='compact'
                            />
                        </div>

                        <div className='translation-section'>
                            <h3 className='translation-section__title'>Typy dokumentů</h3>
                            <TranslationTable
                                fields={docTypeFields}
                                control={control}
                                arrayName="document_types"
                                identityColumns={[
                                    { field: 'option_id', header: 'ID' },
                                    { field: 'code', header: 'Kód' }
                                ]}
                                translationColumns={[{ field: 'document_type_text', header: 'Text překladu', required: true }]}
                                variant='compact'
                            />
                        </div>

                        <div className='translation-section'>
                            <h3 className='translation-section__title'>Docházka</h3>
                            <TranslationTable
                                fields={attendanceFields}
                                control={control}
                                arrayName="attendances"
                                identityColumns={[
                                    { field: 'option_id', header: 'ID' },
                                    { field: 'code', header: 'Kód' }
                                ]}
                                translationColumns={[{ field: 'attendance_text', header: 'Text překladu', required: true }]}
                                variant='compact'
                            />
                        </div>

                        <div className='translation-section'>
                            <h3 className='translation-section__title'>Typy orgánů</h3>
                            <TranslationTable
                                fields={bodyTypeFields}
                                control={control}
                                arrayName="body_types"
                                identityColumns={[
                                    { field: 'option_id', header: 'ID' },
                                    { field: 'code', header: 'Kód' }
                                ]}
                                translationColumns={[
                                    { field: 'typ_organu_text', header: 'Text překladu', required: true },
                                    { field: 'typ_organu_text_genitiv', header: 'Text překladu (2. pád, volitelné)', required: false }
                                ]}
                                variant='compact'
                            />
                        </div>

                        <div className='translation-section'>
                            <h3 className='translation-section__title'>Typy municipalit</h3>
                            <TranslationTable
                                fields={municipalityTypeFields}
                                control={control}
                                arrayName="municipality_types"
                                identityColumns={[
                                    { field: 'option_id', header: 'ID' },
                                    { field: 'code', header: 'Kód' }
                                ]}
                                translationColumns={[
                                    { field: 'typ_municipality_text', header: 'Text překladu', required: true },
                                    { field: 'typ_municipality_text_genitiv', header: 'Text překladu (2. pád, volitelné)', required: false }
                                ]}
                                variant='compact'
                            />
                        </div>
                    </div>

                    <LineDivider />

                    <div className='translation-section translation-section--full'>
                        <h3 className='translation-section__title'>Hlasovací možnosti</h3>
                        <TranslationTable
                            fields={voteOptionFields}
                            control={control}
                            arrayName="vote_options"
                            identityColumns={[
                                { field: 'option_id', header: 'ID' },
                                { field: 'code', header: 'Kód' }
                            ]}
                            translationColumns={[
                                { field: 'male_text', header: 'Mužský tvar', required: true },
                                { field: 'female_text', header: 'Ženský tvar (volitelné)', required: false },
                                { field: 'universal_text', header: 'Univerzální "nehlasoval/a" (volitelné)', required: false }
                            ]}
                            variant='compact'
                        />
                    </div>
                </div>

            <div className='translation-actions'>
                <FormRow type='button'>
                    <div className='flex-right w-full mt-4'>
                        <ButtonAdd
                            label='Uložit překlady'
                            type='submit'
                            disabled={processing}
                            loading={processing}
                        />
                    </div>
                </FormRow>
            </div>
            </FormContainer>
        </form>
    );
};
